import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { letterRegApi, signatureApi, masterApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import SignatureApprovalDialog from '../components/SignatureApprovalDialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const LOGO_URL = (typeof window !== 'undefined' ? window.location.origin : '') + '/assets/aampl-logo.jpg';
const TYPES = [
  { key: 'warning',      label: 'Warning',          bar: '#C2151F', badge: 'bg-[#FDE7E9] text-[#C2151F]', ring: 'border-l-4 border-[#C2151F]' },
  { key: 'ncr',          label: 'Non-Conformity',   bar: '#D9705F', badge: 'bg-[#FEF0EE] text-[#D9705F]', ring: 'border-l-4 border-[#F0A99E]' },
  { key: 'appreciation', label: 'Appreciation',     bar: '#15A36E', badge: 'bg-[#E7F7EF] text-[#15A36E]', ring: 'border-l-4 border-[#15A36E]' },
  { key: 'appointment',  label: 'Appointment Letter', bar: '#8A5A12', badge: 'bg-[#FFF3D6] text-[#8A5A12]', ring: 'border-l-4 border-[#E9A23B]' },
  { key: 'confirmation', label: 'Confirmation',     bar: '#15307F', badge: 'bg-[#E8EFFD] text-[#15307F]', ring: 'border-l-4 border-[#15307F]' },
  { key: 'transfer',     label: 'Transfer Letter',  bar: '#2563EB', badge: 'bg-[#EFF6FF] text-[#1D4ED8]', ring: 'border-l-4 border-[#2563EB]' },
  { key: 'other',        label: 'Other',            bar: '#5A6377', badge: 'bg-[#EFF1F6] text-[#5A6377]', ring: 'border-l-4 border-[#CBD2E0]' },
];
const NEUTRAL_BADGE = { Promotion: 'bg-[#E8EFFD] text-[#15307F]', Transfer: 'bg-[#EFF1F6] text-[#11182B]', 'Department Reassignment': 'bg-[#EFF1F6] text-[#11182B]', Offer: 'bg-[#FFF3D6] text-[#8A5A12]' };
const NEUTRAL_RING = { Promotion: 'border-l-4 border-[#15307F]', Transfer: 'border-l-4 border-[#5A6377]', 'Department Reassignment': 'border-l-4 border-[#5A6377]', Offer: 'border-l-4 border-[#E9A23B]' };

const empMeta = (t) => TYPES.find((x) => x.key === t) || TYPES[TYPES.length - 1];

const todayIso = () => new Date().toISOString().slice(0, 10);

const inr = (v) => (v == null ? '' : `₹${Number(v).toLocaleString('en-IN')}`);

export default function LettersTab({ employeeId, canEdit }) {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState('ncr');
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayIso);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  // Transfer letters only: the destination, from the Unit master.
  const [units, setUnits] = useState([]);
  const [toUnit, setToUnit] = useState('');
  const [fromUnit, setFromUnit] = useState('');
  // Who signs. Chosen per letter from Assistant Manager level upwards.
  const [signatories, setSignatories] = useState([]);
  const [signedBy, setSignedBy] = useState('');
  // Optional co-signatory, for letters two people sign.
  const [signedBy2, setSignedBy2] = useState('');
  // Letters that carry the stored signature need the signatory's emailed
  // approval before they will print signed. Held in memory only, ~15 min.
  const [signToken, setSignToken] = useState('');
  const [signAsk, setSignAsk] = useState(null);   // the letter awaiting approval
  const [signSentTo, setSignSentTo] = useState('');
  const [signBusy, setSignBusy] = useState(false);
  // Only the confirmation letter carries the stored signature here. The
  // appointment letter is issued for wet signature; the offer letter lives
  // in Pre-joining and is gated there.
  const SIGNED_TYPES = ['confirmation'];
  const isConfirmation = type === 'confirmation';
  const isAppointment = type === 'appointment';
  const isTransfer = type === 'transfer';
  // These letters write themselves from the employee record and need a date.
  const isAuto = isConfirmation || isAppointment || isTransfer;

  // Probation status, derived server-side from the stored date of joining.
  const [elig, setElig] = useState(null);
  const [searchParams] = useSearchParams();

  // Preselect the letter type when linked in, e.g. the confirmation-due prompt
  // sends HR here with ?letter=confirmation.
  useEffect(() => {
    const l = searchParams.get('letter');
    if (l && TYPES.some((t) => t.key === l)) setType(l);
  }, [searchParams]);

  const load = useCallback(async () => {
    try { const r = await letterRegApi.allLetters(employeeId); setRows(r.data || []); }
    catch (e) { toast.error('Failed to load letters'); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    letterRegApi.signatories()
      .then((r) => setSignatories(r.data.signatories || []))
      .catch(() => setSignatories([]));
  }, []);

  useEffect(() => {
    if (!isTransfer || units.length) return;
    masterApi.units().then((r) => setUnits(r.data || [])).catch(() => setUnits([]));
  }, [isTransfer, units.length]);

  useEffect(() => {
    if (!isConfirmation) { setElig(null); return; }
    let live = true;
    letterRegApi.confirmationEligibility(employeeId)
      .then((r) => {
        if (!live) return;
        setElig(r.data);
        // Confirm from the day probation actually completed, not today.
        if (r.data?.due_date) setEffectiveDate(r.data.due_date);
      })
      .catch(() => { if (live) setElig(null); });
    return () => { live = false; };
  }, [employeeId, isConfirmation]);

  const add = async () => {
    // A confirmation letter writes itself from the employee record, so the
    // reason box is optional there; every other type needs one.
    if (!isAuto && !reason.trim()) { toast.error('Enter the reason'); return; }
    if (isTransfer && !toUnit) { toast.error('Select the unit to transfer this employee to'); return; }
    if (isTransfer && fromUnit && fromUnit === toUnit) {
      toast.error('The current unit and the transfer unit are the same'); return;
    }
    if (isAuto && !effectiveDate) {
      toast.error(isTransfer ? 'Pick the transfer effective date'
        : isAppointment ? 'Pick the appointment date' : 'Pick the confirmation date');
      return;
    }
    // The backend enforces this too; failing here just saves a round trip.
    if (isConfirmation && elig && !elig.eligible) {
      toast.error(elig.reason || 'This employee has not completed probation yet.');
      return;
    }
    setBusy(true);
    try {
      await letterRegApi.add(employeeId, {
        type,
        reason: reason.trim(),
        ...(isAuto ? { effective_date: effectiveDate, address: address.trim() } : {}),
        ...(isTransfer ? { to_unit: toUnit, from_unit: fromUnit } : {}),
        signatory_employee_id: signedBy || undefined,
        signatory_employee_id_2: signedBy2 || undefined,
      });
      setReason(''); setAddress(''); setToUnit(''); setFromUnit('');
      load();
      toast.success(isAuto ? 'Letter created' : 'Letter recorded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  // Letters filed in their own register (offers) are deleted through the
  // register endpoint; letters on the employee's own record have their own.
  // Anything not named here has no delete route and must not offer the button.
  const DELETABLE_REGISTER = { offer: 'offer' };
  const canDelete = (l) => l.source === 'employee_letter' || !!DELETABLE_REGISTER[l.source];

  const remove = async (l) => {
    if (!canDelete(l)) return;
    if (!window.confirm(
      l.source === 'offer'
        ? 'Delete this offer letter from the register? The candidate record is not affected.'
        : 'Delete this letter from record?')) return;
    try {
      if (l.source === 'employee_letter') await letterRegApi.remove(l.id);
      else await letterRegApi.removeRegister(DELETABLE_REGISTER[l.source], l.id);
      // Awaited, so "deleted" is only said once the list has actually been
      // refetched. Fire-and-forget put the toast on screen while the old rows
      // were still showing, which reads as a delete that did not work.
      await load();
      toast.success('Letter deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openHtmlInTab = (html) => {
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const requestSignOtp = async (l) => {
    setSignBusy(true);
    try {
      const r = await signatureApi.requestOtp();
      setSignSentTo(r.data.sent_to);
      setSignAsk(l);
      toast.success(`Approval code sent to ${r.data.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the approval code'); }
    finally { setSignBusy(false); }
  };

  const print = async (l) => {
    // The stored signature is applied automatically by the server.
    await doPrint(l, signToken);
  };

  const doPrint = async (l, tok) => {
    try {
      let r;
      if (l.source === 'employee_letter') r = await letterRegApi.print(l.id, { logo_url: LOGO_URL, sign_token: tok || undefined });
      else if (l.source === 'promotion') r = await letterRegApi.reprintPromotion(l.id, { logo_url: LOGO_URL });
      else if (l.source === 'transfer') r = await letterRegApi.reprintTransfer(l.id, { logo_url: LOGO_URL });
      else if (l.source === 'offer') r = await letterRegApi.reprintOffer(l.id, { logo_url: LOGO_URL, sign_token: tok || undefined });
      else { toast.error('Unknown letter type'); return; }
      openHtmlInTab(r.data.html);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate letter'); }
  };

  const badgeFor = (l) => {
    if (l.source === 'employee_letter') {
      const m = empMeta(l.type);
      return { badge: m.badge, ring: m.ring, label: (l.type === 'warning' ? '⚠ ' : '') + m.label };
    }
    return { badge: NEUTRAL_BADGE[l.kind] || NEUTRAL_BADGE.Transfer, ring: NEUTRAL_RING[l.kind] || NEUTRAL_RING.Transfer, label: l.kind };
  };

  const body = (l) => {
    if (l.source === 'employee_letter') {
      if (l.type === 'confirmation') {
        return `Services confirmed${l.effective_date ? ` with effect from ${l.effective_date}` : ''}`
          + (l.reason ? ` · ${l.reason}` : '');
      }
      if (l.type === 'transfer') {
        return `Transferred${l.to_unit ? ` to ${l.to_unit}` : ''}`
          + (l.effective_date ? ` with effect from ${l.effective_date}` : '');
      }
      if (l.type === 'appointment') {
        return `Appointment${l.effective_date ? ` with effect from ${l.effective_date}` : ''}`
          + (l.reason ? ` · ${l.reason}` : '');
      }
      return l.reason || '';
    }
    if (l.source === 'promotion') return `Designation change: ${l.detail}` + (l.new_salary != null ? ` · new salary ${inr(l.new_salary)}` : '') + (l.effective_date ? ` · effective ${l.effective_date}` : '');
    if (l.source === 'transfer') return (l.detail || '') + (l.effective_date ? ` · effective ${l.effective_date}` : '');
    if (l.source === 'offer') return l.detail || 'Offer of employment';
    return '';
  };

  return (
    <div className="bg-white border border-[#E6E9F0] rounded-xl p-6">
      <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554] mb-1">Letters on record</h2>
      <p className="text-[11px] text-[#98A1B2] mb-4">Every letter on this employee&apos;s file — offer, promotion, transfer, and warning/NCR/appreciation.</p>

      {canEdit && (
        <div className="border border-[#E6E9F0] rounded-lg p-3 mb-5 bg-[#F7F8FB]">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <select data-testid="letter-type-select" value={type} onChange={(e) => setType(e.target.value)} className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5">
              {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <span className="text-[11px] text-[#98A1B2]">
              {isTransfer
                ? 'Pick the destination unit — its name and address fill the subject and the whole body.'
                : isAppointment
                  ? 'Name, designation and department fill in automatically. All 16 terms are included. Issued for wet signature.'
                  : isConfirmation
                    ? 'Name, date of joining and designation fill in automatically from the employee record.'
                    : 'Type the reason — it goes into the letter body.'}
            </span>
          </div>

          {isConfirmation && elig && (
            <div data-testid="confirmation-eligibility"
                 className={`mb-2 rounded-md px-3 py-2 text-[12px] border ${
                   elig.eligible
                     ? 'bg-[#E7F7EF] border-[#BFE7D5] text-[#0F7A52]'
                     : 'bg-[#FFF7E6] border-[#F3DDB0] text-[#8A5A12]'}`}>
              {elig.eligible ? (
                <>
                  <b>Eligible for confirmation.</b>{' '}
                  Completed {elig.months} months on {formatDate(elig.due_date)}
                  {elig.doj ? ` (joined ${formatDate(elig.doj)})` : ''}.
                </>
              ) : (
                <>
                  <b>Not yet eligible.</b> {elig.reason}
                </>
              )}
            </div>
          )}

          <div className="mb-3">
            <label className="text-[11px] text-[#5A6377] block">
              <span className="block mb-1 font-medium">Signing authority</span>
              <select data-testid="letter-signatory" value={signedBy}
                      onChange={(e) => { setSignedBy(e.target.value); if (!e.target.value) setSignedBy2(''); }}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5 min-w-[300px]">
                <option value="">Select who signs this letter…</option>
                {signatories.map((sgn) => (
                  <option key={sgn.id} value={sgn.id}>
                    {sgn.name} — {sgn.designation}{sgn.unit ? ` · ${sgn.unit}` : ''}
                  </option>
                ))}
              </select>
            </label>
            {signedBy && (
              <label className="text-[11px] text-[#5A6377] block mt-2">
                <span className="block mb-1 font-medium">
                  Second signature <span className="font-normal text-[#98A1B2]">(optional)</span>
                </span>
                <select data-testid="letter-signatory-2" value={signedBy2}
                        onChange={(e) => setSignedBy2(e.target.value)}
                        className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5 min-w-[300px]">
                  <option value="">Signed by one person only</option>
                  {signatories.filter((sgn) => sgn.id !== signedBy).map((sgn) => (
                    <option key={sgn.id} value={sgn.id}>
                      {sgn.name} — {sgn.designation}{sgn.unit ? ` · ${sgn.unit}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <p className="text-[11px] text-[#98A1B2] mt-1">
              Assistant Manager level and above. Their name and designation print above the signing
              line; the letter is issued for a wet signature.
              {signedBy2 && ' Two signatures print side by side.'}
            </p>
          </div>

          {isTransfer && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-3">
                <label className="text-[11px] text-[#5A6377] block">
                  <span className="block mb-1 font-medium">Current unit</span>
                  <select data-testid="letter-transfer-from-unit" value={fromUnit}
                          onChange={(e) => setFromUnit(e.target.value)}
                          className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5 min-w-[240px]">
                    <option value="">From the employee record</option>
                    {units.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-[#5A6377] block">
                  <span className="block mb-1 font-medium">Transfer to unit</span>
                  <select data-testid="letter-transfer-unit" value={toUnit}
                          onChange={(e) => setToUnit(e.target.value)}
                          className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5 min-w-[240px]">
                    <option value="">Select unit…</option>
                    {units.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </select>
                </label>
              </div>
              <p className="text-[11px] text-[#98A1B2] mt-1">
                C.C. goes to Accounts Department (with this employee&apos;s personal file), the Gate
                Office (with their department) and Production. For the full form with a preview,
                use <b>Records → Transfer Letter</b>.
              </p>
            </div>
          )}

          {isAuto && (
            <div className="flex flex-wrap gap-3 mb-2">
              <label className="text-[11px] text-[#5A6377]">
                <span className="block mb-1 font-medium">
                  {isTransfer ? 'Transfer with effect from'
                    : isAppointment ? 'Appointment with effect from' : 'Confirmed with effect from'}
                </span>
                <input data-testid="letter-effective-date" type="date" value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5" />
              </label>
              <label className="text-[11px] text-[#5A6377] flex-1 min-w-[220px]">
                <span className="block mb-1 font-medium">Address <span className="text-[#98A1B2]">(optional — not stored on the employee record)</span></span>
                <input data-testid="letter-address" type="text" value={address}
                  onChange={(e) => setAddress(e.target.value)} placeholder="Village, District, State, PIN"
                  className="w-full border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5" />
              </label>
            </div>
          )}

          <textarea data-testid="letter-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder={isAuto ? 'Optional extra paragraph — leave blank for the standard letter.' : 'Reason for this letter…'}
            className="w-full border border-[#E6E9F0] rounded-md p-2 text-sm" />
          <div className="flex justify-end mt-2">
            <Button data-testid="letter-record-button" size="sm" onClick={add}
              disabled={busy || (isConfirmation && elig !== null && !elig.eligible)}>
              {isTransfer ? 'Create transfer letter'
                : isAppointment ? 'Create appointment letter'
                  : isConfirmation ? 'Create confirmation letter' : 'Record letter'}
            </Button>
          </div>
        </div>
      )}

      {signAsk && (
        <SignatureApprovalDialog
          sentTo={signSentTo}
          onClose={() => setSignAsk(null)}
          onSkip={() => { const l = signAsk; setSignAsk(null); doPrint(l, ''); }}
          onDone={(tok) => { setSignToken(tok); const l = signAsk; setSignAsk(null); doPrint(l, tok); }}
        />
      )}

      {rows.length === 0 ? <p className="text-[#98A1B2] py-6 text-center">No letters on record.</p> : (
        <div className="space-y-2" data-testid="letters-list">
          {rows.map((l) => {
            const m = badgeFor(l);
            return (
              <div key={`${l.source}-${l.id}`} data-testid={`letter-row-${l.source}-${l.id}`} className={`rounded-lg p-3 bg-white border border-[#EEF1F6] ${m.ring}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${m.badge}`}>{m.label}</span>
                    <p className="text-sm text-[#11182B] mt-1.5 whitespace-pre-wrap">{body(l)}</p>
                    <p className="text-[11px] text-[#98A1B2] mt-1">{(l.letter_date || l.created_at || '').slice(0, 10)}{l.issued_by_name ? ` · by ${l.issued_by_name}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-3 whitespace-nowrap">
                    <button data-testid={`letter-print-${l.source}-${l.id}`} onClick={() => print(l)} className="text-xs text-[#2563EB] hover:underline">{l.source === 'employee_letter' ? 'Print' : 'Reprint'}</button>
                    {canEdit && canDelete(l) && <button data-testid={`letter-delete-${l.id}`} onClick={() => remove(l)} className="text-xs text-[#C2151F] hover:underline">Delete</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
